/**
 * ChecklistSection 確認檢核 (S2.3) — the validation checklist card. Renders
 * every confirm-gate item (never short-circuited) with localized blocker
 * rows. Two sources, server-first:
 * - the SERVER checklist returned by a failed confirm attempt (authoritative);
 * - otherwise the client mirror over detail-page reads (checklist.ts).
 * Explicitly NO customer-item authorization row, and price deviation is NOT
 * here — pending_approval blocks fulfillment, not entry (plan §S2.1).
 */

import { Alert, Card, Spin, Typography, theme } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { SectionHeader } from '@/components/products/ProductDetailPage/sections/SectionHeader';
import { radiusTokens } from '@/styles/tokens';
import type { ConfirmChecklistResult, SalesOrder } from '@/types/api/salesOrder';
import { localizeChecklistBlockers } from '../checklist';

const { Text } = Typography;

interface ChecklistSectionProps {
  so: SalesOrder;
  /** Client mirror (null while its feeds load). */
  localChecklist: ConfirmChecklistResult | null;
  /** Authoritative result from the last failed confirm attempt, if any. */
  serverChecklist: ConfirmChecklistResult | null;
  feedsLoading: boolean;
}

export const ChecklistSection = ({
  so,
  localChecklist,
  serverChecklist,
  feedsLoading,
}: ChecklistSectionProps) => {
  const { t } = useTranslation(['orders', 'common']);
  const { token } = theme.useToken();

  const checklist = serverChecklist ?? localChecklist;

  return (
    <div>
      <SectionHeader
        title={t('orders:so.detail.sections.checklist')}
        subtitle={t('orders:so.detail.checklist.subtitle')}
      />

      {serverChecklist && (
        <Alert
          className="tw-mb-4"
          type="info"
          showIcon
          message={t('orders:so.detail.checklist.serverNote')}
        />
      )}

      {!checklist || feedsLoading ? (
        <div className="tw-flex tw-justify-center tw-py-8">
          <Spin />
        </div>
      ) : (
        <>
          {checklist.ok && so.status === 'draft' && (
            <Alert
              className="tw-mb-4"
              type="success"
              showIcon
              message={t('orders:so.detail.checklist.passHint')}
            />
          )}
          <div className="tw-flex tw-flex-col tw-gap-3">
            {checklist.items.map((item) => {
              const blockers = localizeChecklistBlockers(item, so);
              return (
                <Card
                  key={item.key}
                  size="small"
                  style={{
                    borderRadius: radiusTokens.panel,
                    borderColor: item.ok ? token.colorBorderSecondary : token.colorErrorBorder,
                  }}
                >
                  <div className="tw-flex tw-items-start tw-gap-3">
                    {item.ok ? (
                      <CheckCircleFilled
                        style={{ color: token.colorSuccess, fontSize: token.sizeMD, marginTop: 2 }}
                      />
                    ) : (
                      <CloseCircleFilled
                        style={{ color: token.colorError, fontSize: token.sizeMD, marginTop: 2 }}
                      />
                    )}
                    <div className="tw-flex-1 tw-min-w-0">
                      <Text strong>{t(`orders:so.detail.checklist.items.${item.key}`)}</Text>
                      {item.key === 'credit' && (
                        <Text
                          type="secondary"
                          style={{ display: 'block', fontSize: token.fontSizeSM }}
                        >
                          {t('orders:so.detail.checklist.creditStubNote')}
                        </Text>
                      )}
                      {blockers.map((blocker, index) => (
                        <Text
                          key={`${blocker.key}-${index}`}
                          style={{
                            display: 'block',
                            fontSize: token.fontSizeSM,
                            color: token.colorError,
                          }}
                        >
                          {t(blocker.key, blocker.values)}
                        </Text>
                      ))}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ChecklistSection;
